import { useState, useMemo, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import ApiKeyManagementModalView from "./view";
import { useNotification } from "@context/NotificationProvider";
import { data } from "@components/Features/Media/ReportBuilder/sample-data/sample-data";
import { CreateAdminApiKeyRequest } from "@api/api_key_management/admin";
import { CreateApiKeyRequest } from "@api/api_key_management/user";

interface ApiKey {
  id: number;
  name: string;
  key?: string;
  key_prefix: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface ApiKeyFormData {
  name: string;
  expiryDate: string | null;
}

interface ExpiryOption {
  key: string;
  value: string;
  text: string;
}

interface LoadingState {
  loading: boolean;
  silent: boolean;
}

interface ApiKeyManagementModalProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  userData: { value: { id: number } } | null;
  isAdmin: boolean;
  fetchApiKeys: (userId?: number) => Promise<ApiKey[] | any>;
  createApiKey: (
    formData: CreateAdminApiKeyRequest | CreateApiKeyRequest
  ) => Promise<ApiKey | any>;
  revokeApiKey: (keyId: number) => Promise<void | any>;
}

export default function ApiKeyManagementModal({
  isOpen,
  setOpen,
  userData,
  isAdmin = false,
  fetchApiKeys: fetchApiKeysFn,
  createApiKey: createApiKeyFn,
  revokeApiKey: revokeApiKeyFn,
}: ApiKeyManagementModalProps) {
  const { setNotification } = useNotification();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    loading: false,
    silent: false,
  });
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [itemsLoading, setItemsLoading] = useState<number[]>([]);

  const [formData, setFormData] = useState<ApiKeyFormData>({
    name: "",
    expiryDate: "",
  });

  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [newKeyData, setNewKeyData] = useState<ApiKey | null>(null);

  const columnHelper = createColumnHelper<ApiKey>();

  const expiryOptions: ExpiryOption[] = [
    { key: "never", value: "never", text: "Never" },
    { key: "1", value: "1", text: "1 day" },
    { key: "7", value: "7", text: "7 days" },
    { key: "30", value: "30", text: "30 days" },
    { key: "60", value: "60", text: "60 days" },
    { key: "90", value: "90", text: "90 days" },
    { key: "365", value: "365", text: "1 year" },
  ];

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        id: "name",
        header: () => "Name",
        cell: (info) => info.getValue(),
        meta: {
          cellClassName: "s-padding",
          width: "25%",
        },
      }),
      columnHelper.accessor("key_prefix", {
        id: "key_prefix",
        header: () => "Key Prefix",
        cell: (info) => <span>{info.getValue()}...</span>,
        meta: {
          cellClassName: "s-padding",
          width: "15%",
        },
      }),
      columnHelper.accessor("last_used_at", {
        id: "last_used_at",
        header: () => "Last Used",
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <span style={{ opacity: 0.5 }}>Never</span>;
          return new Date(value).toLocaleDateString();
        },
        meta: {
          cellClassName: "s-padding",
          width: "20%",
        },
      }),
      columnHelper.accessor("expires_at", {
        id: "expires_at",
        header: () => "Expires",
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <span style={{ opacity: 0.5 }}>Never</span>;
          const expiryDate = new Date(value);
          const isExpired = expiryDate < new Date();
          return (
            <span style={{ color: isExpired ? "#d9534f" : "inherit" }}>
              {expiryDate.toLocaleDateString()}
              {isExpired && " (Expired)"}
            </span>
          );
        },
        meta: {
          cellClassName: "s-padding",
          width: "20%",
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => "",
        cell: ({ row }) => (
          <button
            // className="btn btn--danger btn--small"
            className="api-key-management__revoke-button"
            onClick={() => handleRevokeKey(row.original.id)}
            disabled={itemsLoading.includes(row.original.id)}
          >
            {itemsLoading.includes(row.original.id) ? "Revoking..." : "Revoke"}
          </button>
        ),
        meta: {
          cellClassName: "s-padding",
          style: {
            textAlign: "right",
            paddingRight: 20,
          },
          width: "20%",
        },
      }),
    ];
  }, [itemsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormChange = (
    field: keyof ApiKeyFormData,
    value: string
  ): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateKey = async () => {
    if (!formData.name.trim()) {
      // TODO: Add validation notification
      return;
    }

    setCreateLoading(true);

    try {
      let dataToSend: CreateAdminApiKeyRequest | CreateApiKeyRequest;

      if (userData?.value?.id) {
        dataToSend = {
          name: formData.name,
          expires_in_days: formData.expiryDate
            ? parseInt(formData.expiryDate)
            : undefined,
          user_id: String(userData.value.id),
        } as CreateAdminApiKeyRequest;
      } else {
        dataToSend = {
          name: formData.name,
          expires_in_days: formData.expiryDate
            ? parseInt(formData.expiryDate)
            : undefined,
        } as CreateApiKeyRequest;
      }

      const newKey = await createApiKeyFn(dataToSend);

      if (!newKey || newKey?.error)
        throw new Error(newKey?.error.message || "Failed to create API key.");

      if (newKey?.canceled) {
        setCreateLoading(false);
        return;
      }

      setNewKeyData(newKey);
      setApiKeys((prev) => [...prev, newKey]);
      setShowSuccessMessage(true);
      setFormData({ name: "", expiryDate: "" });
    } catch (error) {
      let message = (error as Error)?.message;
      setNotification({
        message,
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }

    setCreateLoading(false);
  };

  const handleRevokeKey = async (keyId: number): Promise<void> => {
    setItemsLoading((prev) => [...prev, keyId]);

    try {
      const result = await revokeApiKeyFn(keyId);

      if (result?.error) throw new Error(result.error.message);
      if (result?.canceled) {
        setItemsLoading((prev) => prev.filter((id) => id !== keyId));
        return;
      }

      setApiKeys((prev) => prev.filter((key) => key.id !== keyId));
    } catch (error) {
      let message = (error as Error).message;
      setNotification({
        message,
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }

    setItemsLoading((prev) => prev.filter((id) => id !== keyId));
  };

  const hydrateApiKeys = async (): Promise<void> => {
    setLoading({ loading: true, silent: false });

    try {
      if (isAdmin && !userData?.value?.id)
        throw new Error("User ID is required to fetch API keys");

      const keys = isAdmin
        ? await fetchApiKeysFn(userData!.value.id)
        : await fetchApiKeysFn();

      if (keys?.error) throw new Error(keys.error.message);
      if (keys?.canceled) {
        setLoading({ loading: false, silent: false });
        return;
      }

      setApiKeys(keys);
    } catch (error) {
      let message = (error as Error).message;
      setNotification({
        message,
        iconName: "ban",
        hex: "var(--danger-1)",
      });
      setApiKeys([]);
    }

    setLoading({ loading: false, silent: false });
  };

  useEffect(() => {
    if (isOpen) {
      hydrateApiKeys();
      // Reset success message when modal opens
      setShowSuccessMessage(false);
      setNewKeyData(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ApiKeyManagementModalView
      isOpen={isOpen}
      setOpen={setOpen}
      apiKeys={apiKeys}
      columns={columns}
      loading={loading}
      formData={formData}
      handleFormChange={handleFormChange}
      handleCreateKey={handleCreateKey}
      createLoading={createLoading}
      expiryOptions={expiryOptions}
      handleRevokeKey={handleRevokeKey}
      itemsLoading={itemsLoading}
      showSuccessMessage={showSuccessMessage}
      setShowSuccessMessage={setShowSuccessMessage}
      newKeyData={newKeyData}
    />
  );
}
